import React, { useContext, useState, useEffect, createContext } from 'react';
import { auth } from '../firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFile, makeApiRequest, uploadFile } from '../api';

// Create the AuthContext
const AuthContext = createContext();

// Hook to use the AuthContext
export function useAuth() {
  return useContext(AuthContext);
}

// AuthProvider component to wrap your app
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  async function sendAuthenticatedRequest(
    endpoint,
    method = 'GET',
    data = null
  ) {
    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    const token = await currentUser.getIdToken();
    return makeApiRequest(endpoint, method, data, token);
  }

  async function uploadAuthenticatedFile(endpoint, file, data = {}) {
    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    const token = await currentUser.getIdToken();
    return uploadFile(endpoint, file, data, token);
  }

  async function getAuthenticatedFile(endpoint) {
    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    const token = await currentUser.getIdToken();
    return getFile(endpoint, token);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    logout,
    sendAuthenticatedRequest,
    uploadAuthenticatedFile,
    getAuthenticatedFile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
